import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { adminApi, contractsApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import type { ContractFields, ContractKind, ServiceContract } from '@/api/types';
import { useAuth } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Plus } from 'lucide-react';
import { Button } from '@/design-system/Button';
import { InlineError } from '@/design-system/Feedback';
import { Select, TextArea, TextInput } from '@/design-system/Inputs';
import { Dialog } from '@/design-system/Overlay';
import { formatDate } from '@/lib/format';
import { QuickVendorDialog } from '@/features/assets/QuickCatalogDialogs';
import {
  AssetPicker,
  CONTRACT_KIND,
  isoDate,
  optionsOf,
  useVendors,
} from '@/features/maintenance/MaintenanceUi';
import { useRefreshMaintenance } from '@/features/maintenance/WorkOrderDialogs';

/** The day after `date`, as `yyyy-mm-dd`. */
function dayAfter(date?: string) {
  if (!date) return isoDate();
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/** One year on from `date`, less a day: the usual end of an annual term. */
function yearTermEnd(start: string) {
  const end = new Date(`${start}T00:00:00Z`);
  end.setUTCFullYear(end.getUTCFullYear() + 1);
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

/**
 * Who gets the renewal reminder. Everyone can choose themselves or nobody; someone who can see the
 * user list can choose anyone.
 */
function useOwnerOptions() {
  const { user, can } = useAuth();
  const canListUsers = can(PERMISSIONS.USER_READ);
  const params = { size: 200 };
  const users = useQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: () => adminApi.users(params),
    enabled: canListUsers,
    staleTime: 5 * 60_000,
  });
  return useMemo(() => {
    const options = [{ value: '', label: 'Nobody - no reminder' }];
    if (user?.userId) options.push({ value: user.userId, label: `Me (${user.displayName ?? 'you'})` });
    (users.data?.content ?? [])
      .filter((account) => account.enabled && account.id !== user?.userId)
      .forEach((account) => options.push({ value: account.id, label: account.displayName }));
    return { options, me: user?.userId ?? '' };
  }, [users.data, user]);
}

/**
 * Recording a contract, or changing the terms of one that is active.
 */
export function ContractDialog({
  contract,
  assetId,
  onClose,
  onSaved,
}: {
  /** Present when editing. */
  contract?: ServiceContract;
  /** Pre-selects the covered asset when recording from an asset's page. */
  assetId?: string;
  onClose: () => void;
  onSaved?: (contract: ServiceContract) => void;
}) {
  const refresh = useRefreshMaintenance();
  const { can } = useAuth();
  const canEditCatalogue = can(PERMISSIONS.CATALOG_WRITE);
  const [addingVendor, setAddingVendor] = useState(false);
  const vendors = useVendors();
  const owners = useOwnerOptions();
  const editing = Boolean(contract);
  const [kind, setKind] = useState<ContractKind>(contract?.kind ?? 'AMC');
  const [title, setTitle] = useState(contract?.title ?? '');
  const [vendorId, setVendorId] = useState(contract?.vendorId ?? '');
  const [vendorReference, setVendorReference] = useState(contract?.vendorReference ?? '');
  const [startsOn, setStartsOn] = useState(contract?.startsOn ?? isoDate());
  const [endsOn, setEndsOn] = useState(contract?.endsOn ?? yearTermEnd(isoDate()));
  const [cost, setCost] = useState(contract?.cost === undefined ? '' : String(contract.cost));
  const [currencyCode, setCurrencyCode] = useState(contract?.currencyCode ?? 'INR');
  const [coverage, setCoverage] = useState(contract?.coverage ?? '');
  const [responseHours, setResponseHours] = useState(
    contract?.responseHours === undefined ? '' : String(contract.responseHours),
  );
  const [noticeDays, setNoticeDays] = useState(String(contract?.renewalNoticeDays ?? 30));
  const [ownerUserId, setOwnerUserId] = useState(editing ? (contract?.ownerUserId ?? '') : owners.me);
  const [assets, setAssets] = useState<string[]>(assetId ? [assetId] : []);

  const save = useMutation({
    mutationFn: () => {
      const terms: ContractFields = {
        title: title.trim(),
        vendorId: vendorId || undefined,
        vendorReference: vendorReference.trim() || undefined,
        startsOn,
        endsOn,
        cost: cost === '' ? undefined : Number(cost),
        currencyCode: cost === '' ? undefined : currencyCode.trim().toUpperCase(),
        coverage: coverage.trim() || undefined,
        responseHours: responseHours === '' ? undefined : Number(responseHours),
        renewalNoticeDays: Number(noticeDays),
        ownerUserId: ownerUserId || undefined,
      };
      return contract?.id
        ? contractsApi.update(contract.id, terms)
        : contractsApi.create({ kind, terms, assetIds: assets });
    },
    onSuccess: (saved) => {
      refresh();
      toast.success(editing ? 'Contract updated' : `Contract ${saved.reference} recorded`);
      onSaved?.(saved);
      onClose();
    },
  });

  const blocked = title.trim() === '' || !startsOn || !endsOn || endsOn < startsOn;

  return (
    <>
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${contract?.reference}` : 'Record a warranty or AMC'}
      description="The owner is reminded when the renewal falls due."
      busy={save.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={blocked} onClick={() => save.mutate()}>
            {editing ? 'Save changes' : 'Record contract'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {save.error ? <InlineError error={save.error} /> : null}

        <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <Select label="Kind" value={kind} disabled={editing} options={optionsOf(CONTRACT_KIND)}
            onChange={(event) => setKind(event.target.value as ContractKind)} />
          <TextInput label="Title" required maxLength={200} value={title}
            placeholder="e.g. Laptop fleet AMC 2026"
            onChange={(event) => setTitle(event.target.value)} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select label="Vendor" value={vendorId} options={vendors.optionsFor(vendorId)} disabled={!vendors.canRead}
                onChange={(event) => setVendorId(event.target.value)} />
            </div>
            {canEditCatalogue ? (
              <Button
                variant="secondary"
                aria-label="Add a vendor"
                title="Add a vendor"
                onClick={() => setAddingVendor(true)}
                className="mb-px h-9 shrink-0 px-3"
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </div>
          <TextInput label="Vendor's contract number" maxLength={100} value={vendorReference}
            onChange={(event) => setVendorReference(event.target.value)} />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextInput label="Starts" type="date" required value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)} />
          <TextInput label="Ends" type="date" required value={endsOn}
            error={endsOn && startsOn && endsOn < startsOn ? 'Ends before it starts' : undefined}
            onChange={(event) => setEndsOn(event.target.value)} />
          <TextInput label="Remind (days before end)" type="number" min={0} max={365} value={noticeDays}
            onChange={(event) => setNoticeDays(event.target.value)} />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextInput label="Cost" type="number" min={0} step="0.01" value={cost}
            onChange={(event) => setCost(event.target.value)} />
          <TextInput label="Currency" maxLength={3} value={currencyCode}
            onChange={(event) => setCurrencyCode(event.target.value)} />
          <TextInput label="Response time (hours)" type="number" min={1} value={responseHours}
            onChange={(event) => setResponseHours(event.target.value)} />
        </div>

        <TextArea label="What is covered" rows={2} maxLength={2000} value={coverage}
          placeholder="Parts and labour, on-site next business day…"
          onChange={(event) => setCoverage(event.target.value)} />

        <Select label="Renewal reminder goes to" value={ownerUserId} options={owners.options}
          onChange={(event) => setOwnerUserId(event.target.value)} />

        {!editing && <AssetPicker multiple label="Covered assets" value={assets} onChange={setAssets} />}
      </div>
    </Dialog>

    <QuickVendorDialog
      open={addingVendor}
      onClose={() => setAddingVendor(false)}
      onCreated={(vendor) => {
        setAddingVendor(false);
        setVendorId(vendor.id);
      }}
    />
    </>
  );
}

/** Renewing for another term. The new term covers the same assets; this one keeps its record. */
export function RenewContractDialog({
  contract,
  onClose,
  onRenewed,
}: {
  contract: ServiceContract;
  onClose: () => void;
  onRenewed?: (next: ServiceContract) => void;
}) {
  const refresh = useRefreshMaintenance();
  const start = dayAfter(contract.endsOn);
  const [startsOn, setStartsOn] = useState(start);
  const [endsOn, setEndsOn] = useState(yearTermEnd(start));
  const [cost, setCost] = useState(contract.cost === undefined ? '' : String(contract.cost));
  const [currencyCode, setCurrencyCode] = useState(contract.currencyCode ?? 'INR');
  const [vendorReference, setVendorReference] = useState('');

  const renew = useMutation({
    mutationFn: () =>
      contractsApi.renew(contract.id!, {
        startsOn,
        endsOn,
        cost: cost === '' ? undefined : Number(cost),
        currencyCode: cost === '' ? undefined : currencyCode.trim().toUpperCase(),
        vendorReference: vendorReference.trim() || undefined,
      }),
    onSuccess: (next) => {
      refresh();
      toast.success(`Renewed as ${next.reference}`);
      onRenewed?.(next);
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Renew ${contract.reference}`}
      description={`The current term ends ${formatDate(contract.endsOn)}. The new term covers the same ${contract.assetIds?.length ?? 0} assets.`}
      busy={renew.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={renew.isPending}>
            Back
          </Button>
          <Button variant="primary" loading={renew.isPending} disabled={!endsOn || endsOn < startsOn}
            onClick={() => renew.mutate()}>
            Renew contract
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {renew.error ? <InlineError error={renew.error} /> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput label="New term starts" type="date" required value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)} />
          <TextInput label="New term ends" type="date" required value={endsOn}
            onChange={(event) => setEndsOn(event.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput label="New price" type="number" min={0} step="0.01" value={cost}
            onChange={(event) => setCost(event.target.value)} />
          <TextInput label="Currency" maxLength={3} value={currencyCode}
            onChange={(event) => setCurrencyCode(event.target.value)} />
        </div>
        <TextInput label="Vendor's new contract number" maxLength={100} value={vendorReference}
          description="Leave empty to keep the current one."
          onChange={(event) => setVendorReference(event.target.value)} />
      </div>
    </Dialog>
  );
}

/** Changing which assets a contract covers. */
export function CoverageDialog({ contract, onClose }: { contract: ServiceContract; onClose: () => void }) {
  const refresh = useRefreshMaintenance();
  const [assets, setAssets] = useState<string[]>(contract.assetIds ?? []);
  const save = useMutation({
    mutationFn: () => contractsApi.cover(contract.id!, assets),
    onSuccess: () => {
      refresh();
      toast.success('Coverage updated');
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Assets covered by ${contract.reference}`}
      busy={save.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save coverage
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {save.error ? <InlineError error={save.error} /> : null}
        <AssetPicker multiple label="Covered assets" value={assets} onChange={setAssets} />
      </div>
    </Dialog>
  );
}
